import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, Box, Sparkles } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { productApi } from '../lib/api';
import SEO from '../components/SEO';
import RecommendationList from '../components/RecommendationList';
import { useApp } from '../context/AppContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const getImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http')) return url;
  return `${BACKEND_URL}${url}`;
};

export default function Home() {
  const { user } = useApp();
  const [categories, setCategories] = useState([]);
  const [heroImages, setHeroImages] = useState([]);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [categoriesRes, heroRes] = await Promise.all([
          productApi.getCategories(),
          productApi.getHeroImages()
        ]);
        setCategories(categoriesRes.data);
        setHeroImages(heroRes.data);
      } catch (error) {
        console.error('Failed to fetch home data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    if (heroImages.length <= 1) return;
    const interval = setInterval(() => {
      setCurrentImageIndex((prev) => (prev + 1) % heroImages.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [heroImages.length]);

  const categoryImages = {
    'keychains': 'https://images.unsplash.com/photo-1678081624272-f41898308070?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDN8MHwxfHNlYXJjaHwyfHxjdXRlJTIwM2QlMjBwcmludGVkJTIwa2V5Y2hhaW4lMjBjaGFyYWN0ZXJ8ZW58MHx8fHwxNzY3ODk2MTI4fDA&ixlib=rb-4.1.0&q=85',
    'toys': 'https://images.unsplash.com/photo-1620062110593-4b3c37545aff?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1NzZ8MHwxfHNlYXJjaHwyfHwzZCUyMHByaW50ZWQlMjBhcnRpY3VsYXRlZCUyMGRyYWdvbiUyMHRveXxlbnwwfHx8fDE3Njc4OTYxMDd8MA&ixlib=rb-4.1.0&q=85',
    'mobile-stand': 'https://images.unsplash.com/photo-1601220363009-f7e66d095649?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzh8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjAzZCUyMHByaW50ZWQlMjBwaG9uZSUyMHN0YW5kfGVufDB8fHx8MTc2Nzg5NjExMXww&ixlib=rb-4.1.0&q=85',
    'laptop-stand': 'https://images.unsplash.com/photo-1658624234283-13d7d2ab76ed?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzd8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBsYXB0b3AlMjBzdGFuZCUyMG9uJTIwZGVza3xlbnwwfHx8fDE3Njc4OTYxMjl8MA&ixlib=rb-4.1.0&q=85',
    'vase': 'https://images.unsplash.com/photo-1767498051839-c7fba864e657?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzd8MHwxfHNlYXJjaHwxfHxhcnRpc3RpYyUyMDNkJTIwcHJpbnRlZCUyMHZhc2UlMjBtb2Rlcm58ZW58MHx8fHwxNzY3ODk2MTAzfDA&ixlib=rb-4.1.0&q=85'
  };

  return (
    <div className="min-h-screen pt-28 md:pt-32" data-testid="home-page">
      <SEO title="Home" description="Explore our curated collection of 3D printed masterpieces, from functional accessories to decorative marvels." />
      <section className="relative min-h-[90vh] flex items-center overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0 pointer-events-none" style={{ background: 'radial-gradient(circle at 10% 20%, rgba(236, 72, 153, 0.17), transparent 38%), radial-gradient(circle at 85% 30%, rgba(6, 182, 212, 0.16), transparent 42%), radial-gradient(circle at 50% 80%, rgba(139, 92, 246, 0.12), transparent 55%)' }} />
        
        <div className="max-w-7xl mx-auto px-6 md:px-12 w-full relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7 }}
              className="space-y-8 relative z-20"
            >
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/30 text-primary text-sm font-mono tracking-wider uppercase" data-testid="hero-badge">
                <Sparkles className="h-4 w-4" />
                Crafted with Precision
              </div>
              
              <h1 className="text-5xl md:text-7xl font-bold tracking-tight leading-tight" data-testid="hero-title">
                <span className="animate-gradient-text">Ved3D</span>
                <span className="block mt-2 animate-gradient-text">
                  Creations
                </span>
              </h1>
              
              <p className="text-base md:text-xl leading-relaxed text-muted-foreground max-w-xl" data-testid="hero-description">
                Transform your imagination into tangible art. Explore our curated collection of 3D printed masterpieces, from functional accessories to decorative marvels.
              </p>

              <div className="flex flex-col sm:flex-row items-center gap-4 pt-4">
                <Button 
                  onClick={() => navigate('/products')}
                  className="w-full sm:w-auto rounded-full bg-primary hover:bg-primary/90 text-white px-8 py-6 text-lg shadow-[0_0_20px_rgba(139,92,246,0.3)] hover:shadow-[0_0_30px_rgba(139,92,246,0.5)] hover:-translate-y-1 transition-all duration-300"
                  data-testid="explore-button"
                >
                  Explore Collections
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
                <Button
                  asChild
                  variant="outline"
                  className="w-full sm:w-auto rounded-full px-8 py-6 text-lg border-white/20 bg-white/5 hover:bg-white/10"
                >
                  <Link to="/contact">Custom Order Inquiry</Link>
                </Button>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 pt-3">
                <div className="glass-panel rounded-2xl px-4 py-3">
                  <p className="text-xs uppercase tracking-widest text-muted-foreground">Collections</p>
                  <p className="text-2xl font-bold">{categories.length || '--'}</p>
                </div>
                <div className="glass-panel rounded-2xl px-4 py-3">
                  <p className="text-xs uppercase tracking-widest text-muted-foreground">Made in India</p>
                  <p className="text-2xl font-bold">100%</p>
                </div>
                <div className="glass-panel rounded-2xl px-4 py-3 col-span-2 sm:col-span-1">
                  <p className="text-xs uppercase tracking-widest text-muted-foreground">Design-first</p>
                  <p className="text-2xl font-bold">3D+</p>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.7, delay: 0.2 }}
              className="relative"
            >
              <div className="relative z-10 aspect-square hero-shine">
                <AnimatePresence mode="wait">
                  {heroImages.length > 0 && (
                    <motion.img
                      key={currentImageIndex}
                      src={getImageUrl(heroImages[currentImageIndex]?.url)}
                      alt={heroImages[currentImageIndex]?.title || "3D Printed Products"}
                      className="w-full h-full object-cover rounded-3xl neon-border"
                      initial={{ opacity: 0, scale: 0.95 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.95 }}
                      transition={{ duration: 0.5 }}
                      data-testid="hero-carousel-image"
                    />
                  )}
                </AnimatePresence>
                
                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2 z-20" data-testid="carousel-indicators">
                  {heroImages.map((_, idx) => (
                    <button
                      key={idx}
                      onClick={() => setCurrentImageIndex(idx)}
                      className={`w-2 h-2 rounded-full transition-all duration-300 ${
                        currentImageIndex === idx 
                          ? 'bg-primary w-8' 
                          : 'bg-white/50 hover:bg-white/80'
                      }`}
                      data-testid={`carousel-indicator-${idx}`}
                    />
                  ))}
                </div>
              </div>
              <div className="absolute -z-10 top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[120%] h-[120%] bg-primary/20 blur-[100px] rounded-full" />
            </motion.div>
          </div>
        </div>
      </section>

      <section className="py-32 px-6 md:px-12" data-testid="categories-section">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-semibold tracking-tight mb-4" data-testid="categories-title">
              Explore Categories
            </h2>
            <p className="text-base md:text-lg text-muted-foreground" data-testid="categories-subtitle">
              Discover unique 3D printed creations across various collections
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {loading ? (
              // Skeleton Loaders
              [...Array(6)].map((_, i) => (
                <div key={i} className="space-y-4">
                  <Skeleton className="aspect-square rounded-2xl w-full" />
                  <Skeleton className="h-8 w-3/4 mx-auto" />
                </div>
              ))
            ) : categories.length > 0 ? (
              categories.map((category, index) => (
                <motion.div
                  key={category.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  data-testid={`category-card-${category.slug}`}
                >
                  <Link to={`/products?category=${category.slug || category.id}`}>
                    <div className="group relative overflow-hidden rounded-3xl glass-panel hover:border-primary/30 transition-all duration-500 hover:-translate-y-1">
                      <div className="aspect-square overflow-hidden">
                        <img
                          src={getImageUrl(category.image || categoryImages[category.slug] || 'https://images.unsplash.com/photo-1620062110593-4b3c37545aff')}
                          alt={category.name}
                          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                          loading="lazy"
                        />
                      </div>
                      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent flex items-end p-6">
                        <div>
                          <h3 className="text-2xl md:text-3xl font-medium text-white mb-2">{category.name}</h3>
                          <p className="text-sm text-white/70">{category.description || 'Explore Collections'}</p>
                        </div>
                        <ArrowRight className="absolute top-6 right-6 h-6 w-6 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                      </div>
                    </div>
                  </Link>
                </motion.div>
              ))
            ) : (
              <div className="col-span-full text-center py-20" data-testid="no-categories">
                <Box className="h-16 w-16 mx-auto mb-4 text-muted-foreground" />
                <p className="text-xl text-muted-foreground">No categories available yet</p>
              </div>
            )}
          </div>
        </div>
      </section>

      <section className="px-6 md:px-12 pb-20">
        <div className="max-w-7xl mx-auto">
          {user && <RecommendationList type="personal" title="Personalized for You" />}
        </div>
      </section>

      {/* Newsletter Section */}
      <section className="py-20 px-6 md:px-12" data-testid="newsletter-section">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="surface-elevated rounded-3xl border border-white/10 px-6 py-14 md:px-12"
          >
            <h2 className="text-3xl md:text-4xl font-semibold tracking-tight mb-4">Stay Connected</h2>
            <p className="text-base md:text-lg text-muted-foreground mb-8">
              Subscribe to our newsletter for exclusive updates, new products, and special offers
            </p>
            <Button asChild className="rounded-full bg-primary hover:bg-primary/90 text-white px-8 py-6 text-lg shadow-[0_0_20px_rgba(139,92,246,0.3)] hover:shadow-[0_0_30px_rgba(139,92,246,0.5)] transition-all duration-300">
              <Link to="/contact">
                Subscribe Now
              </Link>
            </Button>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
